import NavDropdown from './nav-dropdown';
import { Messages } from '../../utils/message-constants';
import { PageConfigItem, pagesConfig } from '../../shared/pages-config';
import { isAbleToSeeUIElement } from '../../utils/roles/client-roles';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { useUserSettings } from '../../utils/use-user-settings';
import { useState } from 'react';
import { enterpriseUpsellPages } from '../../shared/enterprise-upsell-page/enterprise-pages-with-landing-page-enum';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import AddToDrive from '@mui/icons-material/AddToDrive';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import BookmarkBorderOutlined from '@mui/icons-material/BookmarkBorderOutlined';
import DashboardCustomizeOutlined from '@mui/icons-material/DashboardCustomizeOutlined';
import DevicesOutlined from '@mui/icons-material/DevicesOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import FolderSharedOutlined from '@mui/icons-material/FolderSharedOutlined';
import ImportExportOutlined from '@mui/icons-material/ImportExportOutlined';
import InventoryOutlined from '@mui/icons-material/InventoryOutlined';
import ListOutlined from '@mui/icons-material/ListOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PrecisionManufacturingOutlined from '@mui/icons-material/PrecisionManufacturingOutlined';
import ReceiptOutlined from '@mui/icons-material/ReceiptOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
interface NavLinksProps {
  drawerOpen: boolean;
}

export type LinkTitle =
  | 'Bookmarks'
  | 'Dashboard'
  | 'Users'
  | 'Directory'
  | 'Calendars'
  | 'Drive'
  | 'Devices'
  | 'Logging'
  | 'Report Center'
  | 'Automation'
  | 'Import / Export'
  | 'Administration'
  | 'Insights'
  | 'Billing'
  | '';

export default function NavLinks({ drawerOpen }: NavLinksProps) {
  const { bookmarks, usersTier, activeLabs, isDefault, features, ...allRoles } =
    useUserSettings();
  const [currentNavListOpen, setCurrentNavListOpen] = useState<LinkTitle>('');
  const isUpsellFeaureEnabled = isFeatureEnabled(
    'enterpriseFeatureUpsellUiReact',
    features
  );
  const isUpsellPageExists = (name: string) => {
    return enterpriseUpsellPages.some((page) => page.key === name);
  };

  const isInsightsFeatureEnabled = isFeatureEnabled(
    pagesConfig.insights.requiredFeatureFlag,
    features
  );

  function getLinkRenderState(pageConfig: PageConfigItem): {
    show: boolean;
    disabledReason: string | null;
  } {
    const doesntHavePermission =
      !isAbleToSeeUIElement(
        allRoles[pageConfig.roleGroup],
        pageConfig.requiredPermission
      ) && pageConfig.requiredPermission !== '*';
    const doesntHaveTier = usersTier.rank < pageConfig.minimumTier;
    const isTierFallbackPage = isUpsellPageExists(pageConfig.name);

    if (
      pageConfig.requiredFeatureFlag &&
      !isFeatureEnabled(pageConfig.requiredFeatureFlag, features)
    ) {
      return { show: false, disabledReason: null };
    }

    if (isDefault && doesntHaveTier && !isTierFallbackPage) {
      return {
        show: true,
        disabledReason: Messages.Errors.Tier.Viewing(
          pageConfig.minimumTierDisplayValue
        ),
      };
    }

    if (
      (doesntHaveTier && !(isUpsellFeaureEnabled && isTierFallbackPage)) ||
      doesntHavePermission
    ) {
      return { show: false, disabledReason: null };
    }

    return { show: true, disabledReason: null };
  }

  const gpanelContactsState = getLinkRenderState(pagesConfig.gpanelContacts);
  const commandDashboardState = getLinkRenderState(
    pagesConfig.commandDashboard
  );
  const directorySubLinks = [
    ...[
      pagesConfig.groups,
      pagesConfig.groupTemplates,
      pagesConfig.orgUnits,
      pagesConfig.sharedContacts,
    ]
      .map((page) => ({ page, state: getLinkRenderState(page) }))
      .filter(({ state }) => state.show)
      .map(({ page, state }) => ({
        linkHref: page.link,
        linkTitle: page.name,
        disabledReason: state.disabledReason,
      })),
    ...(activeLabs.includes('gpanelcontacts') && gpanelContactsState.show
      ? [
          {
            linkHref: pagesConfig.gpanelContacts.link,
            linkTitle: pagesConfig.gpanelContacts.name,
            disabledReason: gpanelContactsState.disabledReason,
          },
        ]
      : []),
  ];

  const resourceCalendarPages = [
    pagesConfig.resourceCalendars,
    {
      ...pagesConfig.resourceBuildings,
      link: `${pagesConfig.resourceCalendarsLayout.link}/${pagesConfig.resourceBuildings.link}`,
    },
    {
      ...pagesConfig.resourceFeatures,
      link: `${pagesConfig.resourceCalendarsLayout.link}/${pagesConfig.resourceFeatures.link}`,
    },
    {
      ...pagesConfig.resourceDefaults,
      link: `${pagesConfig.resourceCalendarsLayout.link}/${pagesConfig.resourceDefaults.link}`,
    },
  ];
  const firstVisibleResourceCalendarPage = resourceCalendarPages.find(
    (page) => getLinkRenderState(page).show
  );

  const calendarsState = getLinkRenderState(pagesConfig.calendars);
  const calendarsSubLinks = [
    ...(calendarsState.show
      ? [
          {
            linkHref: pagesConfig.calendars.link,
            linkTitle: pagesConfig.calendars.name,
            disabledReason: calendarsState.disabledReason,
          },
        ]
      : []),
    ...(firstVisibleResourceCalendarPage
      ? [
          {
            linkHref: firstVisibleResourceCalendarPage.link,
            linkTitle: pagesConfig.resourceCalendarsLayout.name,
            disabledReason: null,
          },
        ]
      : []),
    ...[pagesConfig.resourceTemplates, pagesConfig.calendarTransferLogs]
      .map((page) => ({ page, state: getLinkRenderState(page) }))
      .filter(({ state }) => state.show)
      .map(({ page, state }) => ({
        linkHref: page.link,
        linkTitle: page.name,
        disabledReason: state.disabledReason,
      })),
  ];

  const driveSubLinks = [
    pagesConfig.driveExplorer,
    pagesConfig.driveSearch,
    pagesConfig.ownershipTransferLogs,
    pagesConfig.sharedDrives,
  ]
    .map((page) => ({ page, state: getLinkRenderState(page) }))
    .filter(({ state }) => state.show)
    .map(({ page, state }) => ({
      linkHref: page.link,
      linkTitle: page.name,
      disabledReason: state.disabledReason,
    }));

  const devicesSubLinks = [
    pagesConfig.mobileManagement,
    pagesConfig.chromeDevices,
  ]
    .map((page) => ({ page, state: getLinkRenderState(page) }))
    .filter(({ state }) => state.show)
    .map(({ page, state }) => ({
      linkHref: page.link,
      linkTitle: page.name,
      disabledReason: state.disabledReason,
    }));

  const hasSection1Content =
    bookmarks.length > 0 ||
    getLinkRenderState(pagesConfig.users).show ||
    directorySubLinks.length > 0 ||
    calendarsSubLinks.length > 0 ||
    driveSubLinks.length > 0 ||
    devicesSubLinks.length > 0 ||
    getLinkRenderState(pagesConfig.logging).show;

  const reportCenterSubLinks = [pagesConfig.reports, pagesConfig.cPanelLogs]
    .map((page) => ({ page, state: getLinkRenderState(page) }))
    .filter(({ state }) => state.show)
    .map(({ page, state }) => ({
      linkHref: page.link,
      linkTitle: page.name,
      disabledReason: state.disabledReason,
    }));

  const automationSubLinks = [
    pagesConfig.rulesEngine,
    pagesConfig.policies,
    pagesConfig.signatureTemplates,
    pagesConfig.decommissioningLayout,
    pagesConfig.driveSweep,
    pagesConfig.contactSync,
  ]
    .map((page) => ({ page, state: getLinkRenderState(page) }))
    .filter(({ state }) => state.show)
    .map(({ page, state }) => ({
      linkHref: page.link,
      linkTitle: page.name,
      disabledReason: state.disabledReason,
    }));

  const importExportSubLinks = [
    pagesConfig.bulkOps,
    pagesConfig.gmailExport,
    ...(isFeatureEnabled('vaultExportReact', features)
      ? [pagesConfig.vaultExport]
      : []),
    pagesConfig.gmailDeletion,
  ]
    .map((page) => ({ page, state: getLinkRenderState(page) }))
    .filter(({ state }) => state.show)
    .map(({ page, state }) => ({
      linkHref: page.link,
      linkTitle: page.name,
      disabledReason: state.disabledReason,
    }));

  const hasSection2Content =
    reportCenterSubLinks.length > 0 ||
    automationSubLinks.length > 0 ||
    importExportSubLinks.length > 0;

  const settingsSubPages = [
    {
      ...pagesConfig.setGpanelAdmin,
      link: `${pagesConfig.settings.link}/${pagesConfig.setGpanelAdmin.link}`,
    },
    {
      ...pagesConfig.ipIntrusion,
      link: `${pagesConfig.settings.link}/${pagesConfig.ipIntrusion.link}`,
    },
    {
      ...pagesConfig.secondaryDomains,
      link: `${pagesConfig.settings.link}/${pagesConfig.secondaryDomains.link}`,
    },
    {
      ...pagesConfig.timezone,
      link: `${pagesConfig.settings.link}/${pagesConfig.timezone.link}`,
    },
    {
      ...pagesConfig.customLabeling,
      link: `${pagesConfig.settings.link}/${pagesConfig.customLabeling.link}`,
    },
    {
      ...pagesConfig.googleApiStatus,
      link: `${pagesConfig.settings.link}/${pagesConfig.googleApiStatus.link}`,
    },
    {
      ...pagesConfig.sessionTimeout,
      link: `${pagesConfig.settings.link}/${pagesConfig.sessionTimeout.link}`,
    },
    {
      ...pagesConfig.welcomeTemplates,
      link: `${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}`,
    },
  ];

  const firstVisibleSettingsPage = settingsSubPages.find(
    (page) => getLinkRenderState(page).show
  );
  const settingsState = getLinkRenderState(pagesConfig.settings);

  const administrationSubLinks = [
    ...[
      pagesConfig.indexing,
      pagesConfig.labs,
      pagesConfig.roleManager,
      ...(isFeatureEnabled('driveLabelsRead', features)
        ? [pagesConfig.driveLabelsManager]
        : []),
      pagesConfig.api,
    ]
      .map((page) => ({ page, state: getLinkRenderState(page) }))
      .filter(({ state }) => state.show)
      .map(({ page, state }) => ({
        linkHref: page.link,
        linkTitle: page.name,
        disabledReason: state.disabledReason,
      })),
    ...(settingsState.show && firstVisibleSettingsPage
      ? [
          {
            linkHref: firstVisibleSettingsPage.link,
            linkTitle: pagesConfig.settings.name,
            disabledReason: settingsState.disabledReason,
          },
        ]
      : []),
  ];

  const insightsSubPages = [
    {
      ...pagesConfig.insightsCloudInsights,
      link: `/${pagesConfig.insights.link}/${pagesConfig.insightsCloudInsights.link}`,
    },
    {
      ...pagesConfig.insightsDetailViews,
      link: `/${pagesConfig.insights.link}/${pagesConfig.insightsDetailViews.link}`,
    },
    {
      ...pagesConfig.insightsHelpfulInformation,
      link: `/${pagesConfig.insights.link}/${pagesConfig.insightsHelpfulInformation.link}`,
    },
  ];

  const firstVisibleInsightsPage = insightsSubPages.find(
    (page) => getLinkRenderState(page).show
  );

  const insightsSubLinks = insightsSubPages
    .map((page) => ({ page, state: getLinkRenderState(page) }))
    .filter(({ state }) => state.show)
    .map(({ page, state }) => ({
      linkHref: page.link,
      linkTitle: page.name,
      disabledReason: state.disabledReason,
    }));

  const hasSection3Content =
    administrationSubLinks.length > 0 ||
    getLinkRenderState(pagesConfig.billing).show;

  return (
    <List
      component="nav"
      sx={{
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        paddingX: drawerOpen ? 1 : 0.5,
      }}
    >
      <Stack spacing={0.5}>
        {commandDashboardState.show && (
          <NavDropdown
            drawerOpen={drawerOpen}
            linkTitle={pagesConfig.commandDashboard.name}
            linkHref={pagesConfig.commandDashboard.link}
            icon={<DashboardCustomizeOutlined />}
            setCurrentNavListOpen={setCurrentNavListOpen}
            currentNavListOpen={currentNavListOpen}
            disabledReason={commandDashboardState.disabledReason}
          />
        )}
        {bookmarks.length > 0 && (
          <NavDropdown
            drawerOpen={drawerOpen}
            linkTitle={'Bookmarks'}
            linkHref=""
            subLinks={bookmarks.map((bookmarkKey) => {
              const bookmark =
                pagesConfig[bookmarkKey as keyof typeof pagesConfig];

              return {
                linkHref: bookmark?.link || '',
                linkTitle: bookmark?.name || bookmarkKey,
              };
            })}
            icon={<BookmarkBorderOutlined />}
            setCurrentNavListOpen={setCurrentNavListOpen}
            currentNavListOpen={currentNavListOpen}
          />
        )}
        {(() => {
          const { show, disabledReason } = getLinkRenderState(
            pagesConfig.users
          );
          if (!show) return null;
          return (
            <NavDropdown
              drawerOpen={drawerOpen}
              linkTitle={pagesConfig.users.name}
              linkHref={pagesConfig.users.link}
              icon={<PersonOutline />}
              setCurrentNavListOpen={setCurrentNavListOpen}
              currentNavListOpen={currentNavListOpen}
              disabledReason={disabledReason}
            />
          );
        })()}

        <NavDropdown
          drawerOpen={drawerOpen}
          linkTitle={'Directory'}
          linkHref=""
          subLinks={directorySubLinks}
          icon={<FolderSharedOutlined />}
          setCurrentNavListOpen={setCurrentNavListOpen}
          currentNavListOpen={currentNavListOpen}
        />
        <NavDropdown
          drawerOpen={drawerOpen}
          linkTitle={'Calendars'}
          linkHref=""
          subLinks={calendarsSubLinks}
          icon={<EventOutlined />}
          setCurrentNavListOpen={setCurrentNavListOpen}
          currentNavListOpen={currentNavListOpen}
        />
        <NavDropdown
          drawerOpen={drawerOpen}
          linkTitle={'Drive'}
          linkHref=""
          subLinks={driveSubLinks}
          icon={<AddToDrive />}
          setCurrentNavListOpen={setCurrentNavListOpen}
          currentNavListOpen={currentNavListOpen}
        />
        <NavDropdown
          drawerOpen={drawerOpen}
          linkTitle={'Devices'}
          linkHref=""
          subLinks={devicesSubLinks}
          icon={<DevicesOutlined />}
          setCurrentNavListOpen={setCurrentNavListOpen}
          currentNavListOpen={currentNavListOpen}
        />
        {(() => {
          const { show, disabledReason } = getLinkRenderState(
            pagesConfig.logging
          );
          if (!show) return null;
          return (
            <NavDropdown
              drawerOpen={drawerOpen}
              linkTitle={pagesConfig.logging.name}
              linkHref={pagesConfig.logging.link}
              icon={<ListOutlined />}
              setCurrentNavListOpen={setCurrentNavListOpen}
              currentNavListOpen={currentNavListOpen}
              disabledReason={disabledReason}
            />
          );
        })()}
        {hasSection1Content && hasSection2Content && (
          <Divider variant="middle" component="li" />
        )}
        {hasSection2Content && (
          <>
            <NavDropdown
              drawerOpen={drawerOpen}
              linkTitle={'Report Center'}
              linkHref="Report%20Center"
              subLinks={reportCenterSubLinks}
              icon={<InventoryOutlined />}
              setCurrentNavListOpen={setCurrentNavListOpen}
              currentNavListOpen={currentNavListOpen}
            />
            <NavDropdown
              drawerOpen={drawerOpen}
              linkTitle={'Automation'}
              linkHref=""
              subLinks={automationSubLinks}
              icon={<PrecisionManufacturingOutlined />}
              setCurrentNavListOpen={setCurrentNavListOpen}
              currentNavListOpen={currentNavListOpen}
            />
            <NavDropdown
              drawerOpen={drawerOpen}
              linkTitle={'Import / Export'}
              linkHref=""
              icon={<ImportExportOutlined />}
              subLinks={importExportSubLinks}
              setCurrentNavListOpen={setCurrentNavListOpen}
              currentNavListOpen={currentNavListOpen}
            />
          </>
        )}
        {hasSection2Content && hasSection3Content && (
          <Divider variant="middle" component="li" />
        )}
        {hasSection3Content && (
          <>
            <NavDropdown
              drawerOpen={drawerOpen}
              linkTitle={'Administration'}
              linkHref=""
              subLinks={administrationSubLinks}
              icon={<AdminPanelSettingsOutlined />}
              setCurrentNavListOpen={setCurrentNavListOpen}
              currentNavListOpen={currentNavListOpen}
            />
            {isInsightsFeatureEnabled && firstVisibleInsightsPage && (
              <NavDropdown
                drawerOpen={drawerOpen}
                linkTitle={'Insights'}
                linkHref=""
                subLinks={insightsSubLinks}
                icon={<InsightsOutlined />}
                setCurrentNavListOpen={setCurrentNavListOpen}
                currentNavListOpen={currentNavListOpen}
              />
            )}
            {(() => {
              const { show, disabledReason } = getLinkRenderState(
                pagesConfig.billing
              );
              if (!show) return null;
              return (
                <NavDropdown
                  drawerOpen={drawerOpen}
                  linkTitle={pagesConfig.billing.name}
                  linkHref={pagesConfig.billing.link}
                  icon={<ReceiptOutlined />}
                  setCurrentNavListOpen={setCurrentNavListOpen}
                  currentNavListOpen={currentNavListOpen}
                  disabledReason={disabledReason}
                />
              );
            })()}
          </>
        )}
      </Stack>
    </List>
  );
}
